import { useMemo } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { BedDouble, Loader2 } from "lucide-react"
import { LodgingBookingDetail } from "@/components/travel/LodgingBookingDetail"
import { CanvasHeader } from "@/components/canvas/ViewPlaceholder"
import { useActiveEvent } from "@/core/active-event"
import { buildLodging, lodgingQueryOptions } from "@/core/lodging"

// Full-screen lodging-booking detail — the narrow-canvas counterpart to the
// lodging reading-pane split. Distinct /stay segment so it doesn't collide
// with the /lodging list route.
export const Route = createFileRoute("/$orgSlug/$eventSlug/stay/$bookingId")({
  component: BookingDetailView,
})

function BookingDetailView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/stay/$bookingId" })
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()

  const back = () =>
    navigate({
      to: "/$orgSlug/$eventSlug/lodging",
      params: { orgSlug: params.orgSlug, eventSlug: params.eventSlug },
    })

  const lodgingQ = useQuery({ ...lodgingQueryOptions(eventId ?? ""), enabled: !!eventId })
  const bookings = useMemo(() => buildLodging(lodgingQ.data), [lodgingQ.data])
  const bookingId = decodeURIComponent(params.bookingId)
  const booking = useMemo(
    () => bookings.find((b) => b.id === bookingId) ?? null,
    [bookings, bookingId],
  )

  if (!eventId || lodgingQ.isLoading) {
    return (
      <div className="flex h-full flex-col">
        <CanvasHeader title="Lodging" icon={<BedDouble className="h-5 w-5" />} />
        <div className="flex flex-1 items-center justify-center text-plum-700/50 dark:text-plum-200/40">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </div>
    )
  }

  if (!booking) {
    return (
      <div className="flex h-full flex-col">
        <CanvasHeader title="Lodging" icon={<BedDouble className="h-5 w-5" />} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
            Booking not found
          </p>
          <button
            onClick={back}
            className="rounded-lg px-3 py-1.5 text-[13px] font-semibold text-white shadow-fluent-sm"
            style={{ background: "var(--accent)" }}
          >
            Back to lodging
          </button>
        </div>
      </div>
    )
  }

  return <LodgingBookingDetail booking={booking} onClose={back} closeVariant="back" />
}
